import React from "react";
import { Check, TrendingUp, Activity } from "lucide-react";
import { TELEGRAM_URL } from "@/config";
import { trackSubscribe } from "@/lib/track";

const reasons = [
    "Trade after work, no need to monitor charts all day long",
    "Learn from experienced traders who actively analyse the market",
    "Avoid the costly beginner mistakes that destroy most new accounts",
    "Follow a structured learning journey instead of random scattered content",
    "Join a community that keeps you motivated and accountable, day by day",
];

const WhyMTLSection = () => {
    return (
        <section
            id="why"
            data-testid="why-section"
            className="bg-[#F2F2F2] py-20 md:py-28"
        >
            <div className="px-6 md:px-12 lg:px-24">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <span className="inline-block text-xs sm:text-sm font-semibold tracking-widest uppercase text-[#0077b6]">
                            Why MTL
                        </span>
                        <h2
                            data-testid="why-heading"
                            className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-black leading-[1.1]"
                        >
                            Built for working adults
                            <br />
                            who actually want to{" "}
                            <span className="text-[#0077b6]">improve</span>.
                        </h2>
                        <p className="mt-5 text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl">
                            We're not promising you'll quit your job next month.
                            We're promising structure, mentorship and a real
                            community so you finally have a chance to grow as a
                            trader — at your own pace.
                        </p>

                        <ul
                            data-testid="why-list"
                            className="mt-8 space-y-4 max-w-2xl"
                        >
                            {reasons.map((r, i) => (
                                <li
                                    key={i}
                                    data-testid={`why-item-${i}`}
                                    className="flex items-start gap-4"
                                >
                                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#0077b6] text-white shadow-md shadow-[#0077b6]/30">
                                        <Check className="h-4 w-4" />
                                    </span>
                                    <span className="text-base sm:text-lg text-gray-800 leading-relaxed">
                                        {r}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-10">
                            <a
                                href={TELEGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="why-cta"
                                onClick={trackSubscribe}
                                className="btn-primary inline-flex items-center gap-2 bg-[#0077b6] hover:bg-[#005f92] text-white font-semibold rounded-xl px-7 py-4 text-base shadow-xl shadow-[#0077b6]/30 transition-colors"
                            >
                                Start Learning Today
                            </a>
                        </div>
                    </div>

                    {/* Visual card */}
                    <div className="lg:col-span-5 order-1 lg:order-2">
                        <div className="relative">
                            <div className="absolute -inset-6 rounded-[2rem] bg-[#0077b6]/15 blur-3xl" />
                            <div className="relative rounded-3xl bg-black text-white p-7 sm:p-8 shadow-2xl border border-black/10 overflow-hidden">
                                <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[#0077b6]/35 blur-3xl pointer-events-none" />
                                <div className="relative">
                                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60">
                                        <Activity className="h-4 w-4 text-[#3aa6e0]" />
                                        Member dashboard preview
                                    </div>

                                    <div className="mt-6 grid grid-cols-2 gap-4">
                                        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                                            <div className="text-xs text-white/50">
                                                This week's signals
                                            </div>
                                            <div className="font-display mt-2 text-3xl font-bold">
                                                42
                                            </div>
                                            <div className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                                                <TrendingUp className="h-3 w-3" />
                                                +12 vs last week
                                            </div>
                                        </div>
                                        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                                            <div className="text-xs text-white/50">
                                                Live sessions
                                            </div>
                                            <div className="font-display mt-2 text-3xl font-bold">
                                                5
                                            </div>
                                            <div className="text-xs text-white/60 mt-1">
                                                Mon → Fri
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 p-4">
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-white/60">
                                                Today's focus
                                            </span>
                                            <span className="text-[#3aa6e0]">
                                                XAUUSD
                                            </span>
                                        </div>
                                        <div className="mt-3 h-20 relative">
                                            <svg
                                                viewBox="0 0 240 80"
                                                className="w-full h-full"
                                            >
                                                <path
                                                    d="M0,60 C30,45 60,70 90,50 S150,20 180,30 S230,15 240,18"
                                                    fill="none"
                                                    stroke="#3aa6e0"
                                                    strokeWidth="2"
                                                />
                                                <path
                                                    d="M0,60 C30,45 60,70 90,50 S150,20 180,30 S230,15 240,18 L240,80 L0,80 Z"
                                                    fill="rgba(58,166,224,0.15)"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhyMTLSection;
